import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThumbsUp, ThumbsDown, Gift, Sparkles, Gem, Wine } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { motion } from 'motion/react'
import type { GiftSuggestion } from '../../../shared/types/chat'
import type { FeedbackType } from '../types'

interface ImageConfig {
  gradient: string
  Icon: LucideIcon
  iconColor: string
}

const imageConfig: Record<string, ImageConfig> = {
  spa: {
    gradient: 'from-blue-500/20 to-cyan-500/20',
    Icon: Sparkles,
    iconColor: 'text-cyan-400'
  },
  jewelry: {
    gradient: 'from-amber-500/20 to-yellow-500/20',
    Icon: Gem,
    iconColor: 'text-amber-400'
  },
  wine: {
    gradient: 'from-purple-500/20 to-rose-500/20',
    Icon: Wine,
    iconColor: 'text-purple-400'
  },
  default: {
    gradient: 'from-gold/20 to-gold/10',
    Icon: Gift,
    iconColor: 'text-gold'
  }
}

interface Props {
  gift: GiftSuggestion
  onFeedback?: (id: string, feedback: FeedbackType) => void
}

export default function GiftCard({ gift, onFeedback }: Props) {
  const { t } = useTranslation('chat')
  const [feedback, setFeedback] = useState<FeedbackType>(gift.feedback ?? null)

  const handleFeedback = (type: 'up' | 'down') => {
    const newFeedback: FeedbackType = feedback === type ? null : type
    setFeedback(newFeedback)
    onFeedback?.(gift.id, newFeedback)
  }

  const defaultConfig: ImageConfig = { gradient: 'from-gold/20 to-gold/10', Icon: Gift, iconColor: 'text-gold' }
  const config = imageConfig[gift.image ?? 'default'] ?? defaultConfig
  const Icon = config.Icon

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-xl overflow-hidden border border-white/10"
    >
      {/* Image placeholder */}
      <div className={`h-24 bg-gradient-to-br ${config.gradient} flex items-center justify-center`}>
        <Icon size={40} className={config.iconColor} strokeWidth={1.5} />
      </div>

      <div className="p-3">
        {/* Title & Price */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h4 className="text-sm font-medium text-ivory leading-tight">{gift.title}</h4>
          <span className="text-xs font-semibold text-gold whitespace-nowrap">{gift.price}</span>
        </div>

        {/* Description */}
        <p className="text-xs text-pearl/60 mb-2">{gift.description}</p>

        {/* Reasoning */}
        <div className="flex items-start gap-1.5 mb-3 px-2 py-1.5 bg-gold/5 rounded-lg">
          <Gift size={10} className="text-gold mt-0.5 flex-shrink-0" />
          <p className="text-xs text-pearl/50 italic">{gift.reasoning}</p>
        </div>

        {/* Feedback buttons */}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => handleFeedback('up')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                feedback === 'up'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-white/5 text-pearl/50 border border-white/10 hover:border-emerald-500/30 hover:text-emerald-400'
              }`}
            >
              <ThumbsUp size={12} />
              <span>{t('gift.goodPick')}</span>
            </button>
            <button
              onClick={() => handleFeedback('down')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                feedback === 'down'
                  ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                  : 'bg-white/5 text-pearl/50 border border-white/10 hover:border-red-500/30 hover:text-red-400'
              }`}
            >
              <ThumbsDown size={12} />
              <span>{t('gift.notRight')}</span>
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
